// Static content for OBSIDIAN MEDIA
import {
  Globe,
  ShoppingBag,
  Zap,
  Palette,
  Megaphone,
  RefreshCcw,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "business-website",
    icon: Globe,
    title: "Business Website",
    price: "₹15,000+",
    summary:
      "High-conversion marketing sites engineered to sell. Lightning-fast, pixel-tight, built on modern stacks.",
    description:
      "We craft bespoke business websites that stop the scroll and start the sale. From landing pages to full marketing suites, every pixel is placed with intent.",
    deliverables: [
      "Custom design + responsive build",
      "CMS integration (Sanity / Strapi)",
      "Performance score > 95",
      "Basic SEO + analytics",
    ],
  },
  {
    slug: "shopify-store",
    icon: ShoppingBag,
    title: "Shopify Store",
    price: "₹25,000+",
    summary:
      "Conversion-obsessed Shopify builds for DTC brands ready to scale past 7 figures.",
    description:
      "We transform your Shopify store into a revenue engine — custom theme, app stack audit, checkout optimization and a design system that scales.",
    deliverables: [
      "Custom Shopify 2.0 theme",
      "Checkout optimization",
      "App stack consultation",
      "Launch-ready migration",
    ],
  },
  {
    slug: "cro",
    icon: Zap,
    title: "CRO",
    price: "₹20,000+",
    summary:
      "Data-driven conversion rate optimization. We find the leaks and seal them with sharp design.",
    description:
      "Heatmaps, session recordings, A/B tests — we engineer every element of your funnel to squeeze out more revenue from the same traffic.",
    deliverables: [
      "Funnel audit + opportunity map",
      "A/B testing setup",
      "UX redesign of key pages",
      "Monthly reporting",
    ],
  },
  {
    slug: "brand-identity",
    icon: Palette,
    title: "Brand Identity",
    price: "₹10,000+",
    summary:
      "Obsidian-sharp brand systems. Logos, palettes, typography and guidelines that command premium.",
    description:
      "We build identities with edges. From the mark to the motion system, every touchpoint is engineered to feel inevitable and iconic.",
    deliverables: [
      "Logo suite + monograms",
      "Color + type system",
      "Brand guidelines PDF",
      "Social media kit",
    ],
  },
  {
    slug: "social-media-management",
    icon: Megaphone,
    title: "Social Media Management",
    price: "₹15,000+",
    summary:
      "Content that cuts through. We design, schedule and scale your social presence.",
    description:
      "Full-stack social management — from strategy to daily execution. We turn feeds into funnels with cinematic creative and smart copy.",
    deliverables: [
      "Monthly content calendar",
      "Reels + static creatives",
      "Community management",
      "Performance reporting",
    ],
  },
  {
    slug: "website-redesign",
    icon: RefreshCcw,
    title: "Website Redesign",
    price: "₹12,000+",
    summary:
      "Legacy site feeling tired? We rebuild it into a revenue-machine with a sharper, obsidian edge.",
    description:
      "A full audit + redesign of your existing site with modern UX patterns, faster load times and a design language that earns attention.",
    deliverables: [
      "UX audit + redesign",
      "Migration + redirects",
      "Speed + SEO overhaul",
      "Post-launch support",
    ],
  },
];

export const PROCESS_STEPS = [
  {
    num: "01",
    title: "Consultation",
    body:
      "We dive deep into your brand, audience and goals. A sharp brief is the foundation of every great build.",
  },
  {
    num: "02",
    title: "Design",
    body:
      "We craft cinematic mockups — every type-size, hover and micro-interaction planned before a single line of code.",
  },
  {
    num: "03",
    title: "Development",
    body:
      "Clean, fast, scalable code. We ship with performance scores that embarrass competitors.",
  },
  {
    num: "04",
    title: "Launch",
    body:
      "Deploy in 3–5 days. We monitor, iterate and hand over the keys with full documentation.",
  },
];

export const TESTIMONIALS = [
  {
    name: "Mira Kapoor",
    role: "Founder, Papertopia",
    quote:
      "Obsidian turned our kawaii stationery dream into a real Shopify store in under a week. Our launch day did 4× our projected revenue — I'm still recovering.",
  },
  {
    name: "Dev Aryan",
    role: "Head of Brand, Brewhaus Coffee",
    quote:
      "They redesigned our entire site and packaging language. Our subscriber retention jumped 41% in the first quarter. Pure operator-grade work.",
  },
  {
    name: "Sara Yusuf",
    role: "CEO, Zabibi Honey",
    quote:
      "The Obsidian team understood our heritage and translated it into a premium digital experience. Our average order value almost doubled overnight.",
  },
  {
    name: "Aanya Sharma",
    role: "Founder, Lumen Apparel",
    quote:
      "Obsidian rebuilt our Shopify store in 4 days. Conversion jumped 38% in the first month. Zero drama, pure execution.",
  },
  {
    name: "Karthik Reddy",
    role: "CMO, VoltGear",
    quote:
      "The CRO sprint alone paid for the entire engagement twice over. These people obsess over numbers harder than founders do.",
  },
  {
    name: "Naina Khan",
    role: "Founder, Nocturne Skincare",
    quote:
      "Their brand identity work is unreal. Our packaging feels like a museum piece now — customers literally DM us about it.",
  },
];

export const CONTACT_INFO = {
  email: "hello@obsidianmedia.cloud",
  // Provide WhatsApp number with country code and no spaces, e.g. "+919876543210"
  // Leaving empty falls back to /contact page from the floating button.
  whatsapp: "+917078266846",
  phone: "+91 7078266846",
  instagram: "https://www.instagram.com/obsidianmedia.io/",
  twitter: "https://x.com/Obsidianahad",
  linkedin: "https://www.linkedin.com/in/ahad-rahman-obsidian",
};

// Slugs of services that should be highlighted as "Most Popular" in pricing carousel
export const MOST_POPULAR_SLUG = "shopify-store";

export const STATS = [
  { number: "150+", label: "Projects Delivered" },
  { number: "60+", label: "Brands Scaled" },
  { number: "5", label: "Years of Obsession" },
  { number: "3-5", label: "Days Turnaround" },
];

export const PORTFOLIO = [
  {
    id: 1,
    client: "Sweet Hour",
    category: "Shopify Store",
    image:
     "/portfolio/sweethour.jpg",
    tag: "E-commerce",
    aspect: "3/4",
  },
  {
    id: 2,
    client: "Feastie",
    category:"Brand Identity",
    image:
      "/portfolio/feastie.jpg",
    tag: "Identity",
    aspect: "3/4",
  },
  {
    id: 3,
    client: "Marco",
    category:"Shopify Redesign",
    image:
    "/portfolio/marcos.jpg",
    tag: "CRO",
    aspect: "4/3",
  },
  {
    id: 4,
    client: "NeverMind",
    category: "Shopify Website",
    image:
    "/portfolio/nevermind.jpg",
    tag :"Website",
    aspect: "3/4",
  },
  {
    id: 5,
    client: "RawBlox",
    category:"Shopify CRO",
    image:
    "/portfolio/Rawblox.jpg",
    tag: "SMM",
    aspect: "3/4",
  },
  {
    id: 6,
    client: "Next Gen",
    category: "Webs Design",
    image:
    "/portfolio/nextgen.jpg",
    tag: "Redesign",
    aspect: "3/4",
  },
  {
    id: 7,
    client: "Papertopia",
    category: "Shopify Store",
    image:
    "/portfolio/papertopia.jpg",
    tag: "Shopify",
    aspect: "4/3",
  },
  {
    id: 8,
    client: "Brewhaus Coffee",
    category: "Brand + Web",
    image:
    "/portfolio/brew.png",
     tag: "Branding",
    aspect: "4/3",
  },
  {
    id: 9,
    client: "Zabibi Honey",
    category: "Premium DTC",
    image:
          "/portfolio/zabibi.jpg",
    tag: "DTC",
    aspect: "3/4",
  },
  {
    id: 10,
    client: "Hippeas",
    category: "Brand Identity",
    image:
    "/portfolio/hippeas.jpg",
    tag: "Identity",
    aspect: "3/4",
  },
];

export const BLOG_POSTS = [
  {
    slug: "why-obsidian-sites-convert-more",
    title: "Why Obsidian-Dark Sites Convert More in 2026",
    excerpt:
      "Deep-black designs aren't just mood — they're math. Here's why premium DTC brands are switching to dark-first design systems.",
    date: "Feb 12, 2026",
    readTime: "6 min",
    author: "OBSIDIAN Team",
    image:
      "https://images.unsplash.com/photo-1697898783638-d1e81c7ebab1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHw0fHxkYXJrJTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjAzZCUyMHJlbmRlciUyMGJsYWNrfGVufDB8fHx8MTc3Nzc4Mjk0Mnww&ixlib=rb-4.1.0&q=85",
    body: [
      "Dark design used to be a moody aesthetic choice. In 2026, it's a conversion strategy.",
      "Premium DTC brands have found that deep-black palettes with sharp typography create perceived value instantly. Users scan less and trust more. The eye isn't pulled in 12 directions — it follows your CTA.",
      "At OBSIDIAN MEDIA we pair this with cinematic motion and hyper-fast load times. The result? Sites that feel like showrooms, not storefronts.",
      "If your brand competes on craft, your pixels should too.",
    ],
  },
  {
    slug: "3-5-day-delivery-how",
    title: "How We Ship Websites in 3–5 Days Without Cutting Corners",
    excerpt:
      "Speed is not a compromise — it's a system. Here's the exact production stack that lets us deliver premium sites in under a week.",
    date: "Feb 02, 2026",
    readTime: "8 min",
    author: "OBSIDIAN Team",
    image:
      "https://images.unsplash.com/photo-1639195276454-f0ec20b5bcb6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxlY29tbWVyY2UlMjB3ZWJzaXRlJTIwbW9ja3VwJTIwZGFya3xlbnwwfHx8fDE3Nzc3ODI5Mzh8MA&ixlib=rb-4.1.0&q=85",
    body: [
      "Agencies that take 8 weeks to ship a landing page are selling process, not outcomes.",
      "Our production machine uses a modular design system, a pre-built component library and tight scoping to deliver in 3-5 days.",
      "The secret isn't shortcuts — it's removing friction. Fixed-scope sprints, daily async updates and zero scope creep.",
      "Clients get a showroom-grade site before their competitor finishes their discovery call.",
    ],
  },
  {
    slug: "shopify-theme-custom-vs-free",
    title: "Custom Shopify Theme vs Free Theme: The Real Cost",
    excerpt:
      "A free theme costs you nothing upfront — and everything in lifetime revenue. A breakdown of hidden costs.",
    date: "Jan 24, 2026",
    readTime: "5 min",
    author: "OBSIDIAN Team",
    image:
      "https://images.pexels.com/photos/10976285/pexels-photo-10976285.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      "Free Shopify themes are great — for zero-budget projects.",
      "But every growing DTC brand eventually hits the wall: slow load times, generic layouts and limited conversion levers.",
      "A custom Shopify 2.0 theme pays for itself inside 3 months for most 7-figure stores. The math is brutal but simple.",
      "If you're serious about scaling, the cost isn't custom theme — it's NOT having one.",
    ],
  },
  {
    slug: "brand-identity-first-10k",
    title: "Your First ₹10,000 on Brand Identity: Where to Spend",
    excerpt:
      "A founder-friendly guide to getting the highest ROI from a brand identity budget.",
    date: "Jan 12, 2026",
    readTime: "7 min",
    author: "OBSIDIAN Team",
    image:
      "https://images.unsplash.com/photo-1761847246139-4be8a2da69ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxvYnNpZGlhbiUyMHN0b25lJTIwZGFyayUyMGNyeXN0YWwlMjBtYWNyb3xlbnwwfHx8fDE3Nzc3ODI5Mzh8MA&ixlib=rb-4.1.0&q=85",
    body: [
      "A sharp logo is just the entrance. The real identity work is the system underneath.",
      "Spend the first 40% on strategy and verbal identity — before anyone opens Figma. The brief determines the ceiling.",
      "The next 40% on core marks, palette, typography. The last 20% on application — social kit, packaging, web tokens.",
      "Most founders flip these ratios and end up with pretty logos nobody remembers.",
    ],
  },
  {
    slug: "cro-quick-wins",
    title: "5 CRO Quick Wins Every DTC Brand Should Ship This Week",
    excerpt:
      "Low-effort, high-impact conversion tweaks you can deploy before your next payroll cycle.",
    date: "Dec 28, 2025",
    readTime: "4 min",
    author: "OBSIDIAN Team",
    image:
      "https://images.pexels.com/photos/29502364/pexels-photo-29502364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      "1. Sticky Add-to-Cart on mobile PDPs. Lifts conversion 8-12% on average.",
      "2. Free-shipping progress bar in cart drawer. Direct AOV lift.",
      "3. Trust bar under primary CTA. Reduces first-purchase anxiety.",
      "4. Replace stock photography with UGC in top-fold. Huge CVR impact.",
      "5. Load the hero under 1.2s. Every 100ms matters.",
    ],
  },
];

export const CALENDLY_URL =
  "https://calendly.com/obsidianmedia-io/30min?month=2026-05&date=2026-05-20";
